import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLifeAreas } from "../../Explore/lifeAreas";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Upload,
  LibraryBig,
  CheckCircle2,
  PauseCircle,
  Layers,
  FileClock,
  PencilLine,
  RefreshCw,
} from "lucide-react";
import {
  getResearchLibrarySummary,
  listResearchDialogues,
  listResearchImports,
} from "../../../Services/adminResearchLibrary";
import EmptyState from "../../../Components/admin/shared/EmptyState";
import MetricCard from "../../../Components/admin/shared/MetricCard";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Skeleton } from "../../../ui/skeleton";
import ImportReleaseModal from "./ImportReleaseModal";
import { DialogueStatusBadge, ImportStatusBadge, TypeBadge } from "./badges";
import {
  buildListParams,
  displayTitle,
  errorMessage,
  formatCount,
  formatDateTime,
  hasOverrides,
  holdReasonOptions,
  lifeAreaOptions,
} from "./researchLibraryFormat";

const TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "FOUNDATIONAL", label: "Foundational" },
  { value: "COMPOSITE", label: "Composite" },
];

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "attachable", label: "Live" },
  { value: "held", label: "Held" },
];

const PAGE_SIZE = 20;

const SummaryTiles = ({ summary, loading }) => {
  const heldTotal = Object.values(summary?.held || {}).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  );
  const heldBreakdown = holdReasonOptions(summary)
    .slice(0, 2)
    .map((o) => `${formatCount(o.count)} ${o.label}`)
    .join(" · ");
  const latest = summary?.latest_import;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
      <MetricCard
        title="Dialogues"
        value={formatCount(summary?.total)}
        subtitle={Object.entries(summary?.by_type || {})
          .map(([k, v]) => `${formatCount(v)} ${k.toLowerCase()}`)
          .join(" · ")}
        icon={<LibraryBig size={20} />}
        color="blue"
        loading={loading}
      />
      <MetricCard
        title="Live (attachable)"
        value={formatCount(summary?.attachable)}
        icon={<CheckCircle2 size={20} />}
        color="green"
        loading={loading}
      />
      <MetricCard
        title="Held"
        value={formatCount(heldTotal)}
        subtitle={heldBreakdown || undefined}
        icon={<PauseCircle size={20} />}
        color="orange"
        loading={loading}
      />
      <MetricCard
        title="Library version"
        value={summary?.library_version || "—"}
        icon={<Layers size={20} />}
        color="purple"
        loading={loading}
      />
      <MetricCard
        title="Latest import"
        value={latest ? <ImportStatusBadge status={latest.status} /> : "—"}
        subtitle={
          latest
            ? `${latest.release_version || "—"} · ${formatDateTime(
                latest.updated_at || latest.created_at
              )}`
            : "No imports yet"
        }
        icon={<FileClock size={20} />}
        color="blue"
        loading={loading}
      />
    </div>
  );
};

const LifeAreaTable = ({ byLifeArea }) => {
  const rows = Object.entries(byLifeArea || {}).sort((a, b) =>
    a[0].localeCompare(b[0])
  );
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">By life area</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Life area</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Live</TableHead>
              <TableHead className="text-right">Held</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([area, counts]) => (
              <TableRow key={area}>
                <TableCell>{area}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCount(counts?.total)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCount(counts?.attachable)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCount((counts?.total || 0) - (counts?.attachable || 0))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const RecentImports = ({ imports, onOpen }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">Recent imports</CardTitle>
    </CardHeader>
    <CardContent>
      {imports.length === 0 ? (
        <p className="text-sm text-muted-foreground">No imports yet.</p>
      ) : (
        <ul className="divide-y">
          {imports.slice(0, 8).map((imp) => (
            <li key={imp.import_id}>
              <button
                type="button"
                onClick={() => onOpen(imp)}
                className="flex w-full items-center justify-between gap-3 border-0 bg-transparent px-0 py-2 text-left text-sm cursor-pointer hover:text-primary"
              >
                <span className="min-w-0">
                  <span className="font-medium">{imp.release_version || "—"}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {imp.import_id} · {formatDateTime(imp.created_at)}
                  </span>
                </span>
                <ImportStatusBadge status={imp.status} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

const AdminResearchLibrary = () => {
  const lifeAreas = useLifeAreas();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [imports, setImports] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lifeArea, setLifeArea] = useState("all");
  const [dialogueType, setDialogueType] = useState("all");
  const [status, setStatus] = useState("all");
  const [reason, setReason] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [resumeImport, setResumeImport] = useState(null);

  // Debounce search by 300ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const [s, imp] = await Promise.all([
        getResearchLibrarySummary(),
        listResearchImports({ limit: 20 }).catch(() => ({ imports: [] })),
      ]);
      setSummary(s);
      setImports(imp?.imports || []);
    } catch (err) {
      setSummaryError(errorMessage(err, "Failed to load the library summary"));
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listResearchDialogues(
        buildListParams({
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch,
          lifeArea,
          dialogueType,
          status,
          reason: status === "attachable" ? "all" : reason,
        })
      );
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.total_pages || 1);
    } catch (err) {
      setError(errorMessage(err, "Failed to load dialogues"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, lifeArea, dialogueType, status, reason]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    load();
  }, [load]);

  const areaOptions = useMemo(
    () => lifeAreaOptions(summary),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, lifeAreas.length]
  );
  const reasonOptions = useMemo(() => holdReasonOptions(summary), [summary]);
  const filtered =
    Boolean(debouncedSearch) ||
    [lifeArea, dialogueType, status, reason].some((v) => v !== "all");

  const onFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const openImport = (existing = null) => {
    setResumeImport(existing);
    setImportOpen(true);
  };

  const handleImportChanged = () => {
    loadSummary();
    load();
  };

  const openDialogue = (dialogueId) =>
    navigate(`/admin/research-library/${encodeURIComponent(dialogueId)}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Research Library</h1>
          <p className="text-sm text-muted-foreground">
            Research Dialogue Library releases, holds, and member-facing overrides.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              loadSummary();
              load();
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={() => openImport(null)}>
            <Upload className="mr-2 h-4 w-4" /> Import release
          </Button>
        </div>
      </div>

      {summaryError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {summaryError}
        </div>
      )}

      <SummaryTiles summary={summary} loading={summaryLoading && !summary} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <LifeAreaTable byLifeArea={summary?.by_life_area} />
        </div>
        <RecentImports imports={imports} onOpen={openImport} />
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search by ID, title, or topic…"
                className="pl-9"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Select value={lifeArea} onValueChange={onFilter(setLifeArea)}>
              <SelectTrigger className="w-full lg:w-[200px]" aria-label="Life area">
                <SelectValue placeholder="Life area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All life areas</SelectItem>
                {areaOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dialogueType} onValueChange={onFilter(setDialogueType)}>
              <SelectTrigger className="w-full lg:w-[160px]" aria-label="Type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_FILTERS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={onFilter(setStatus)}>
              <SelectTrigger className="w-full lg:w-[150px]" aria-label="Status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={reason}
              onValueChange={onFilter(setReason)}
              disabled={status === "attachable" || reasonOptions.length === 0}
            >
              <SelectTrigger className="w-full lg:w-[220px]" aria-label="Hold reason">
                <SelectValue placeholder="Hold reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any hold reason</SelectItem>
                {reasonOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label} ({formatCount(opt.count)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<LibraryBig size={40} />}
              title={filtered ? "No matching dialogues" : "The library is empty"}
              description={
                filtered
                  ? "No dialogues match your current filters."
                  : "Import a release to populate the Research Dialogue Library."
              }
              action={
                filtered
                  ? undefined
                  : { label: "Import release", onClick: () => openImport(null) }
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Life area</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Overrides</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow
                      key={item.research_dialogue_id}
                      className="cursor-pointer"
                      tabIndex={0}
                      onClick={() => openDialogue(item.research_dialogue_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") openDialogue(item.research_dialogue_id);
                      }}
                    >
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {item.research_dialogue_id}
                      </TableCell>
                      <TableCell className="min-w-[240px]">
                        <div className="font-medium">{displayTitle(item)}</div>
                        {item.title_qualifier && (
                          <div className="text-xs text-muted-foreground">
                            {item.title_qualifier}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <TypeBadge type={item.dialogue_type} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {item.life_area || "—"}
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate" title={item.role}>
                        {item.role || "—"}
                      </TableCell>
                      <TableCell>
                        <DialogueStatusBadge dialogue={item} />
                      </TableCell>
                      <TableCell>
                        {hasOverrides(item) ? (
                          <Badge
                            variant="outline"
                            title={`Edited by ${
                              item.admin_overrides?.updated_by || "an admin"
                            } · ${formatDateTime(item.admin_overrides?.updated_at)}`}
                          >
                            <PencilLine className="mr-1 h-3 w-3" /> Override
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-muted-foreground">
                {formatCount(total)} dialogues · Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ImportReleaseModal
        open={importOpen}
        onOpenChange={(next) => {
          setImportOpen(next);
          if (!next) setResumeImport(null);
        }}
        existingImport={resumeImport}
        onChanged={handleImportChanged}
      />
    </div>
  );
};

export default AdminResearchLibrary;
